import { Strings } from '../constants/Strings';

// Patterns matching backend-generated notification strings. Keep in sync with:
//   backends/emperor/api/helpers/groupcomment-helpers.js          (group/feed comment posts)
//   backends/emperor/api/helpers/send-gathering-reminder-notification.js  (event reminders)
//   backends/emperor/api/controllers/groupevent/create.js          (event created)
//   backends/emperor/api/controllers/groupevent/destroy.js         (event cancelled)
//   backends/emperor/api/controllers/groupevent/update.js          (event time/date changed)
//   backends/emperor/api/controllers/groupeventcomment/create.js   (event comment)
//   backends/emperor/api/controllers/reaction/create.js            (reaction added)
//   backends/emperor/api/controllers/group/create.js               (new group)
//   backends/emperor/api/controllers/proposedgroup/update.js       (group proposal approved)
//   backends/emperor/api/controllers/stream/chat-events.js         (direct message)
//   backends/emperor/api/controllers/referral/create-referral-offer.js (referral offer)
//   backends/emperor/api/controllers/perk/update.js                (perk activated)
// If backend wording changes, these stop matching and the original text is returned
// (a dev-only console.warn fires so we notice).

// --- HTML variants (these come through `notification.contentHTML`) ---
const POSTED_IN_GROUP_HTML = /^<strong>([\s\S]*?)<\/strong> was posted in <strong>([\s\S]*?)<\/strong>\.$/;
const POSTED_IN_FEED_HTML = /^<strong>([\s\S]*?)<\/strong> was posted in <strong>Feed<\/strong>\.$/;
const NEIGHBOR_POSTED_IN_FEED_HTML = /^A neighbor posted in the <strong>Feed Tab<\/strong>\.$/;
const GATHERING_CREATED_HTML = /^A new gathering, <strong>([\s\S]*?)<\/strong> has been created in your group <strong>([\s\S]*?)<\/strong>$/;
const REACTION_TO_COMMENT_HTML = /^<strong>([\s\S]*?)<\/strong> reacted to your comment ([\s\S]*?)$/;
const COMMENT_ON_GATHERING_HTML = /^<strong>([\s\S]*?)<\/strong> has commented on your gathering <strong>([\s\S]*?)<\/strong>$/;
const GATHERING_CANCELED_HTML = /^Aw! The gathering <strong>([\s\S]*?)<\/strong> scheduled for <strong>([\s\S]*?)<\/strong>, has been canceled\.$/;
const GATHERING_TIME_CHANGED_HTML = /^The start time for your gathering, <strong>([\s\S]*?)<\/strong> has been changed\.$/;
const GATHERING_DATE_CANCELLED_WITH_NOTE_HTML = /^Aw! The gathering <strong>([\s\S]*?)<\/strong> scheduled for <strong>([\s\S]*?)<\/strong>, has been cancelled with note "([\s\S]*?)"\.?$/;
const GATHERING_DATE_CANCELLED_HTML = /^Aw! The gathering <strong>([\s\S]*?)<\/strong> scheduled for <strong>([\s\S]*?)<\/strong>, has been cancelled\.?$/;
const GATHERING_REMINDER_HTML = /^A gathering you RSVP'd to <strong>([\s\S]*?)<\/strong> starting <strong>([\s\S]*?)<\/strong>$/;
const NEW_GROUP_CREATED_HTML = /^A new group, <strong>([\s\S]*?)<\/strong>, started in your building - join now!$/;
const GROUP_PROPOSAL_APPROVED_HTML = /^Your suggested group <strong>([\s\S]*?)<\/strong> has been approved!$/;
const DIRECT_MESSAGE_RECEIVED = /^You have a new message from ([\s\S]+?)$/;
// Referral and perk notifications wrap the whole content in a single <strong>.
const REFERRAL_OFFER_CREATED = /^<strong>A new Refer Friends offer has been added for your community\. Tap '?Perks'? in Cobu to learn more!'?<\/strong>$/;
const NEW_PERK_ACTIVATED_HTML = /^<strong>A new offer from ([\s\S]*?) has been added for your community\. Tap '?Perks'? in Cobu to learn more!✨?<\/strong>$/;

// --- Plain-text fallbacks (these come through `notification.content`) ---
const POSTED_IN_GROUP_TEXT = /^"([\s\S]*?)" was posted in "([\s\S]*?)"\.$/;
const POSTED_IN_FEED_TEXT = /^"([\s\S]*?)" was posted in Feed\.$/;
const NEIGHBOR_POSTED_IN_FEED_TEXT = /^A neighbor posted in the Feed Tab$/;
const GATHERING_CREATED_TEXT = /^A new gathering, "([\s\S]*?)" has been created in your group "([\s\S]*?)"$/;

const fill = (template: string, values: Record<string, string>): string =>
  Object.entries(values).reduce(
    (acc, [k, v]) => acc.replace(new RegExp(`{${k}}`, 'g'), v),
    template
  );

/**
 * Translates a backend-generated notification string (HTML or plain) into the
 * current Strings language by matching it against known English templates and
 * re-emitting it with localized template strings. Falls through to the input
 * untouched if no template matches — and warns in dev so we can keep parity
 * with the backend.
 */
export const translateNotification = (input: string | undefined): string | undefined => {
  if (!input) return input;
  const t = Strings.get().notifications.templates;

  let m: RegExpMatchArray | null;

  // HTML variants — checked first (frontend prefers contentHTML)
  m = input.match(POSTED_IN_GROUP_HTML);
  if (m) return fill(t.postedInGroupHtml, { content: m[1], groupName: m[2] });

  m = input.match(POSTED_IN_FEED_HTML);
  if (m) return fill(t.postedInFeedHtml, { content: m[1] });

  if (NEIGHBOR_POSTED_IN_FEED_HTML.test(input)) return t.neighborPostedInFeed;

  m = input.match(GATHERING_CREATED_HTML);
  if (m) return fill(t.gatheringCreatedHtml, { title: m[1], groupName: m[2] });

  m = input.match(REACTION_TO_COMMENT_HTML);
  if (m) return fill(t.reactionToCommentHtml, { name: m[1], emoji: m[2] });

  m = input.match(COMMENT_ON_GATHERING_HTML);
  if (m) return fill(t.commentOnGatheringHtml, { name: m[1], title: m[2] });

  m = input.match(GATHERING_CANCELED_HTML);
  if (m) return fill(t.gatheringCanceledHtml, { title: m[1], date: m[2] });

  m = input.match(GATHERING_TIME_CHANGED_HTML);
  if (m) return fill(t.gatheringTimeChangedHtml, { title: m[1] });

  // Check the "with note" variant before the simpler one (more specific first).
  m = input.match(GATHERING_DATE_CANCELLED_WITH_NOTE_HTML);
  if (m) return fill(t.gatheringDateCancelledWithNoteHtml, { title: m[1], date: m[2], note: m[3] });

  m = input.match(GATHERING_DATE_CANCELLED_HTML);
  if (m) return fill(t.gatheringDateCancelledHtml, { title: m[1], date: m[2] });

  m = input.match(GATHERING_REMINDER_HTML);
  if (m) return fill(t.gatheringReminderHtml, { title: m[1], relTime: m[2] });

  m = input.match(NEW_GROUP_CREATED_HTML);
  if (m) return fill(t.newGroupCreatedHtml, { name: m[1] });

  m = input.match(GROUP_PROPOSAL_APPROVED_HTML);
  if (m) return fill(t.groupProposalApprovedHtml, { title: m[1] });

  if (REFERRAL_OFFER_CREATED.test(input)) return `<strong>${t.referralOfferCreated}</strong>`;

  m = input.match(NEW_PERK_ACTIVATED_HTML);
  if (m) return `<strong>${fill(t.newPerkActivatedHtml, { merchant: m[1] })}</strong>`;

  // Direct message has no HTML version — match against the plain pattern.
  m = input.match(DIRECT_MESSAGE_RECEIVED);
  if (m) return fill(t.directMessageReceived, { name: m[1] });

  // Plain-text fallbacks
  m = input.match(POSTED_IN_GROUP_TEXT);
  if (m) return fill(t.postedInGroupHtml, { content: m[1], groupName: m[2] });

  m = input.match(POSTED_IN_FEED_TEXT);
  if (m) return fill(t.postedInFeedHtml, { content: m[1] });

  if (NEIGHBOR_POSTED_IN_FEED_TEXT.test(input)) return t.neighborPostedInFeed;

  m = input.match(GATHERING_CREATED_TEXT);
  if (m) return fill(t.gatheringCreatedHtml, { title: m[1], groupName: m[2] });

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn('[notificationI18n] No template matched — falling back to original:', input);
  }
  return input;
};
